import React, { useEffect, useMemo, useState } from 'react';
import { db, auth } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { getUserRole, canAccessAdmin } from '../utils/roles';
import {
    CALENDAR_DOC, MONTH_MY, WEEKDAY_MY, HOLIDAY_YEARS,
    klToday, dayKey, daysInMonth, firstWeekday, formatDateMY, weekdayMY,
    daysBetween, normalizeCalendar, loadCampaignCalendarCached,
    clearCalendarCache, buildDayMap, monthEvents, upcomingEvents, prepDate, newEvent,
} from '../data/campaignCalendar';

/* ═══════════════════════════════════════════════════
   Kalendar Kempen — seksyen dalam Dashboard

   Pelanggan: baca sahaja. Admin: butang "Edit" → tambah event sendiri, tulis
   nota pada mana-mana event, atau sorok event auto yang tidak berkaitan.

   Data auto (kempen platform + cuti + tarikh komersial) datang dari
   src/data/campaignCalendar.js — TIADA API luar, jadi kalendar tidak boleh
   jadi kosong walaupun Firestore gagal dibaca.
   ═══════════════════════════════════════════════════ */

/* ⭐ PROP `t` + `lang` — dua bahasa TANPA mengubah rupa lama.
   Halaman v3.9 menghantar fungsi terjemahan masuk; rupa lama
   (GettingStartedView) tidak menghantar apa-apa dan kekal Melayu,
   huruf demi huruf. Sebab penuh: lihat nota sama dalam
   DashboardSections.jsx.

   ⛔ Nama event TIDAK diterjemah. "Hari Raya Aidilfitri", "11.11",
      "Cuti sekolah penggal 2" ialah nama khas Malaysia, dan ia
      datang daripada data/campaignCalendar.js + nota yang admin
      taip sendiri — bukan daripada kod. Menterjemahnya bermakna
      menterjemah kandungan pengguna, bukan antara muka. */
const ambilTeks = (t) => (kunci, lalai, vars) => (t ? t(kunci, vars) : lalai);

/* Nama bulan/hari English hanya diperlukan bila `t` ada DAN bahasa
   aktif English; selain itu senarai Melayu dalam fail data yang
   dipakai. Ia tidak masuk kamus kerana ia bukan ayat produk — ia
   nama kalendar, dan menyimpannya sebagai 19 kunci berasingan
   hanya menambah tempat untuk tersalah taip. */
const MONTH_EN = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAY_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TIER_STYLE = {
    big: {
        dot: 'bg-rose-500',
        pill: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/30',
        text: 'text-rose-600 dark:text-rose-400',
        kunci: 'dashboard.campaign.tierBig',
        label: 'Besar',
    },
    mid: {
        dot: 'bg-amber-500',
        pill: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30',
        text: 'text-amber-600 dark:text-amber-400',
        kunci: 'dashboard.campaign.tierMid',
        label: 'Sederhana',
    },
    small: {
        dot: 'bg-slate-400',
        pill: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-700/40 dark:text-slate-300 dark:border-slate-600',
        text: 'text-slate-500 dark:text-slate-400',
        kunci: 'dashboard.campaign.tierSmall',
        label: 'Kecil',
    },
};

const KIND_ICON = {
    platform: 'local_mall',
    holiday: 'celebration',
    school: 'backpack',
    commerce: 'redeem',
    custom: 'push_pin',
};

const KIND_LABEL = {
    platform: { kunci: 'dashboard.campaign.platform', label: 'Kempen platform' },
    holiday: { kunci: 'dashboard.campaign.holiday', label: 'Cuti umum' },
    school: { kunci: 'dashboard.campaign.school', label: 'Cuti sekolah' },
    commerce: { kunci: 'dashboard.campaign.commerce', label: 'Tarikh komersial' },
    custom: { kunci: 'dashboard.campaign.custom', label: 'Tambahan SESAAT' },
};

const countdownLabel = (T, n) => n < 0 ? T('dashboard.campaign.ongoing', 'Sedang berlangsung')
    : n === 0 ? T('common.today', 'Hari ini')
        : n === 1 ? T('dashboard.campaign.tomorrow', 'Esok')
            : T('dashboard.campaign.daysLeft', `${n} hari lagi`, { n });

// ─── Input ringkas untuk mod edit ─────────────────────
const Field = ({ label, children }) => (
    <label className="flex flex-col gap-1 min-w-0">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
        {children}
    </label>
);

const inputCls = 'w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg px-2.5 py-1.5 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:border-indigo-500';

const CampaignCalendarSection = ({ t, lang }) => {
    const isAdmin = canAccessAdmin(getUserRole(auth.currentUser?.email));

    const T = ambilTeks(t);
    /* Bahasa kalendar hanya bertukar bila halaman memang menghantar `t`.
       Tanpa itu ia rupa lama, dan rupa lama kekal Melayu. */
    const en = !!t && lang === 'en';
    const namaBulan = (m) => (en ? MONTH_EN[m - 1] : MONTH_MY[m - 1]);
    const namaHari = (kunci) => {
        const [y, m, d] = kunci.split('-').map(Number);
        return en ? WEEKDAY_EN[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] : weekdayMY(kunci);
    };
    /* "17 Ogos 2026" / "17 August 2026" — susunan hari-bulan-tahun sama
       dalam kedua-dua bahasa, jadi hanya nama bulan yang bertukar. */
    const tarikhPenuh = (kunci) => {
        if (!en) return formatDateMY(kunci);
        const [y, m, d] = kunci.split('-').map(Number);
        return `${d} ${MONTH_EN[m - 1]} ${y}`;
    };

    const todayKey = klToday();
    const [ty, tm] = useMemo(() => todayKey.split('-').map(Number), [todayKey]);

    const [view, setView] = useState({ y: ty, m: tm });
    const [config, setConfig] = useState(() => normalizeCalendar(null));
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [dayModal, setDayModal] = useState(null);      // day-key | null
    const [selectedDay, setSelectedDay] = useState(null); // tumpukan senarai kanan pada satu hari

    const editing = draft !== null;
    const active = editing ? draft : config;

    useEffect(() => {
        let alive = true;
        loadCampaignCalendarCached()
            .then(d => { if (alive) setConfig(d); })
            .catch(() => { /* fail-safe: lapisan auto tetap papar */ });
        return () => { alive = false; };
    }, []);

    // Tukar bulan sentiasa lepaskan hari terpilih — kalau tidak, senarai kanan
    // kekal kosong sebab hari itu bukan lagi dalam bulan yang dipapar.
    const goToMonth = (y, m) => { setSelectedDay(null); setView({ y, m }); };
    const shift = (delta) => {
        let m = view.m + delta, y = view.y;
        if (m < 1) { m = 12; y--; } else if (m > 12) { m = 1; y++; }
        goToMonth(y, m);
    };

    const dayMap = useMemo(() => buildDayMap(view.y, view.m, active, editing), [view, active, editing]);
    const listed = useMemo(() => monthEvents(view.y, view.m, active, editing), [view, active, editing]);
    const upcoming = useMemo(() => upcomingEvents(config, 4, todayKey), [config, todayKey]);
    // Senarai kanan: seluruh bulan, atau hanya hari yang diklik pada grid.
    const visibleList = useMemo(() => selectedDay
        ? listed.filter(e => selectedDay >= e.date && selectedDay <= (e.until || e.date))
        : listed, [listed, selectedDay]);

    const isCurMonth = view.y === ty && view.m === tm;
    const holidaysMissing = !HOLIDAY_YEARS.includes(view.y);

    // ── Mod edit ──────────────────────────────────────
    const startEdit = () => { setError(null); setDraft(JSON.parse(JSON.stringify(config))); };
    const cancelEdit = () => { setDraft(null); setError(null); };

    const saveEdit = async () => {
        if (!draft) return;
        setSaving(true);
        setError(null);
        try {
            const clean = normalizeCalendar(draft);
            // Buang nota kosong supaya doc tidak membesar tanpa guna
            Object.keys(clean.notes).forEach(k => { if (!String(clean.notes[k]).trim()) delete clean.notes[k]; });
            await setDoc(doc(db, 'config', CALENDAR_DOC), clean);
            setConfig(clean);
            clearCalendarCache();
            setDraft(null);
        } catch (e) {
            console.error('[Kalendar] simpan gagal:', e);
            setError(T('dashboard.campaign.saveFailed',
                `Simpan gagal: ${e?.code || e?.message || 'ralat tidak diketahui'}`,
                { sebab: e?.code || e?.message || '?' }));
        } finally {
            setSaving(false);
        }
    };

    const mutate = (fn) => setDraft(d => { const c = JSON.parse(JSON.stringify(d)); fn(c); return c; });
    const setNote = (key, val) => mutate(d => { d.notes[key] = val; });
    const toggleHidden = (key) => mutate(d => {
        const i = d.hidden.indexOf(key);
        if (i >= 0) d.hidden.splice(i, 1); else d.hidden.push(key);
    });
    const setCustom = (id, field, val) => mutate(d => {
        const e = d.events.find(x => x.id === id);
        if (e) e[field] = val;
    });
    // Keutamaan event AUTO disimpan sebagai override berkunci — kod asal tak diusik.
    const setTier = (key, val) => mutate(d => { d.tiers[key] = val; });
    const addCustom = () => mutate(d => { d.events.push(newEvent(dayKey(view.y, view.m, 1))); });
    const removeCustom = (id) => mutate(d => { d.events = d.events.filter(x => x.id !== id); });

    // ── Grid ──────────────────────────────────────────
    const cells = [];
    const lead = firstWeekday(view.y, view.m);
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth(view.y, view.m); d++) cells.push(d);

    return (
        // Lebar penuh (bukan max-w-5xl macam seksyen Dashboard yang lain) — grid 7
        // lajur perlu ruang; dimampatkan ke 1024px, nama event kena potong.
        <section className="w-full px-6 pt-8 pb-2">
            {/* ── Tajuk ── */}
            <div className="flex items-end justify-between gap-3 mb-5 flex-wrap">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-indigo-500 dark:text-indigo-400 mb-1">
                        {T('dashboard.campaign.eyebrow', 'Rancang Awal')}
                    </p>
                    <h2 className="text-xl font-bold text-slate-800 dark:text-white">
                        {T('dashboard.campaign.title', 'Kalendar Kempen')}
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">{active.intro}</p>
                </div>
                {isAdmin && !editing && (
                    <button onClick={startEdit}
                        className="text-sm font-semibold text-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 flex items-center gap-1 transition-colors">
                        <span className="material-symbols-outlined text-[16px]">edit</span> {T('dashboard.campaign.edit', 'Edit kalendar')}
                    </button>
                )}
                {editing && (
                    <div className="flex items-center gap-2">
                        <button onClick={cancelEdit} disabled={saving}
                            className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40">
                            {T('common.cancel', 'Batal')}
                        </button>
                        <button onClick={saveEdit} disabled={saving}
                            className="px-3 py-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold disabled:opacity-40">
                            {saving ? T('common.saving', 'Menyimpan…') : T('common.save', 'Simpan')}
                        </button>
                    </div>
                )}
            </div>

            {error && (
                <p className="mb-3 text-sm text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 rounded-lg px-3 py-2">{error}</p>
            )}

            {/* ── Akan datang ── */}
            {upcoming.length > 0 && !editing && (
                <div className="mb-5">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
                        {T('dashboard.campaign.upcoming', 'Akan datang')}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                        {upcoming.map(e => {
                            const st = TIER_STYLE[e.tier];
                            const prep = prepDate(e);
                            const prepAway = daysBetween(todayKey, prep);
                            return (
                                <button key={e.key} onClick={() => { goToMonth(+e.date.slice(0, 4), +e.date.slice(5, 7)); setSelectedDay(e.date); setDayModal(e.date); }}
                                    className="text-left bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-3 hover:border-indigo-300 dark:hover:border-indigo-600 hover:shadow-sm transition-all">
                                    <div className="flex items-center gap-1.5 mb-1.5">
                                        <span className={`w-2 h-2 rounded-full ${st.dot} flex-shrink-0`}></span>
                                        <span className="text-[11px] font-bold text-slate-400">{countdownLabel(T, e.daysAway)}</span>
                                    </div>
                                    <p className="text-sm font-bold text-slate-800 dark:text-white leading-snug">{e.title}</p>
                                    <p className="text-[11px] text-slate-400 mt-0.5">{tarikhPenuh(e.date)}</p>
                                    {prepAway > 0 ? (
                                        <p className="text-[11px] text-indigo-500 dark:text-indigo-400 mt-1.5 font-medium">
                                            {T('dashboard.campaign.startContent',
                                                `Mula konten ${tarikhPenuh(prep).replace(` ${e.date.slice(0, 4)}`, '')}`,
                                                { tarikh: tarikhPenuh(prep).replace(` ${e.date.slice(0, 4)}`, '') })}
                                        </p>
                                    ) : e.daysAway >= 0 ? (
                                        <p className="text-[11px] text-rose-500 mt-1.5 font-semibold">
                                            {T('dashboard.campaign.prepStarted', 'Masa sedia konten sudah bermula')}
                                        </p>
                                    ) : null}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Kalendar + senarai bersebelahan ──
                Sengaja DUA lajur: event kempen jarang (± 6 event / 31 hari), jadi grid
                yang diregang penuh lebar hanya memaparkan kotak kosong. Grid kekal
                padat untuk tunjuk CORAK sebulan (minggu mana padat); senarai di sebelah
                bawa butiran yang benar-benar dibaca — tarikh, lead time, nota. */}
            {/* Tinggi dikunci sama (xl ke atas) supaya bahagian bawah dua kotak rata.
                Kotak hari guna auto-rows-fr → ia regang isi baki ruang, jadi bulan
                5-minggu dan 6-minggu dua-dua penuh tanpa ruang tergantung. */}
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] gap-4">

                {/* ── Kiri: grid bulanan (padat, bulatan sahaja) ── */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 flex flex-col xl:h-[540px]">
                    <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                        <div className="flex items-center gap-1.5">
                            <button onClick={() => shift(-1)} aria-label={T('dashboard.campaign.prevMonth', 'Bulan sebelum')}
                                className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500">
                                <span className="material-symbols-outlined text-[18px]">chevron_left</span>
                            </button>
                            <span className="text-sm font-bold text-slate-700 dark:text-slate-200 w-32 text-center">{namaBulan(view.m)} {view.y}</span>
                            <button onClick={() => shift(1)} aria-label={T('dashboard.campaign.nextMonth', 'Bulan seterusnya')}
                                className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500">
                                <span className="material-symbols-outlined text-[18px]">chevron_right</span>
                            </button>
                            {!isCurMonth && (
                                <button onClick={() => goToMonth(ty, tm)}
                                    className="text-[11px] px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800">
                                    {T('common.today', 'Hari ini')}
                                </button>
                            )}
                        </div>
                    </div>

                    {holidaysMissing && isAdmin && (
                        <p className="mb-3 text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-lg px-3 py-2">
                            {T('dashboard.campaign.holidaysMissing',
                                `Cuti umum ${view.y} belum diisi — hanya kempen platform dan tarikh komersial dipapar. Ambil senarai rasmi dari kabinet.gov.my, kemudian masukkan dalam src/data/campaignCalendar.js.`,
                                { tahun: view.y })}
                        </p>
                    )}

                    <div className="grid grid-cols-7 gap-1 mb-1">
                        {(en ? WEEKDAY_EN : WEEKDAY_MY).map(w => <div key={w} className="text-center text-[10px] text-slate-400 font-semibold py-0.5">{w}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1 flex-1 min-h-0 auto-rows-fr">
                        {cells.map((d, i) => {
                            if (d == null) return <div key={`e${i}`} className="min-h-[54px] sm:min-h-[62px]"></div>;
                            const key = dayKey(view.y, view.m, d);
                            const evs = dayMap[key] || [];
                            const isToday = key === todayKey;
                            const has = evs.length > 0;
                            const isSel = key === selectedDay;
                            return (
                                <div key={key} onClick={has ? () => setSelectedDay(key) : undefined}
                                    title={has ? evs.map(e => e.title).join(' · ') : undefined}
                                    className={`min-h-[54px] sm:min-h-[62px] rounded-lg border p-1 flex flex-col transition-colors ${isSel ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/25 dark:border-indigo-500'
                                        : isToday ? 'border-indigo-400 dark:border-indigo-500 ring-1 ring-indigo-300 dark:ring-indigo-600 bg-slate-50/50 dark:bg-slate-800/30'
                                            : 'border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30'} ${has ? 'cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-600' : ''}`}>
                                    <span className={`text-[11px] leading-none ${isToday || isSel ? 'font-bold text-indigo-600 dark:text-indigo-400' : 'text-slate-400'}`}>{d}</span>
                                    <div className="flex flex-wrap gap-1 mt-auto">
                                        {evs.slice(0, 4).map(e => (
                                            <span key={e.key} className={`w-2 h-2 rounded-full ${TIER_STYLE[e.tier].dot} ${e.hidden ? 'opacity-30' : ''}`}></span>
                                        ))}
                                        {evs.length > 4 && <span className="text-[9px] leading-none text-slate-400 font-semibold">+{evs.length - 4}</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="flex items-center gap-3 text-[11px] text-slate-500 flex-wrap mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-500 inline-block"></span>{T(TIER_STYLE.big.kunci, TIER_STYLE.big.label)}</span>
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block"></span>{T(TIER_STYLE.mid.kunci, TIER_STYLE.mid.label)}</span>
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-slate-400 inline-block"></span>{T(TIER_STYLE.small.kunci, TIER_STYLE.small.label)}</span>
                        <span className="text-slate-400">{T('dashboard.campaign.legendHint', '· Klik hari untuk tumpu pada event tersebut')}</span>
                    </div>
                </div>

                {/* ── Kanan: senarai event bulan ── */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 flex flex-col xl:h-[540px]">
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <h3 className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                            {T('dashboard.campaign.eventsOf', `Event ${namaBulan(view.m)} ${view.y}`,
                                { bulan: namaBulan(view.m), tahun: view.y })}
                        </h3>
                        {selectedDay && (
                            <button onClick={() => setSelectedDay(null)}
                                className="text-[11px] px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800">
                                {T('dashboard.campaign.showAll', 'Papar semua')}
                            </button>
                        )}
                    </div>

                    <div className="space-y-1.5 flex-1 min-h-0 overflow-y-auto pr-1 max-xl:max-h-[440px]">
                        {visibleList.map(e => {
                            const st = TIER_STYLE[e.tier];
                            const last = e.until || e.date;
                            const past = last < todayKey;
                            const prep = prepDate(e);
                            const note = e.kind === 'custom' ? e.hint : e.note;
                            const away = daysBetween(todayKey, e.date);
                            return (
                                <button key={e.key} onClick={() => setDayModal(e.date)}
                                    className={`w-full text-left rounded-xl border p-2.5 transition-colors hover:border-indigo-300 dark:hover:border-indigo-600 hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 ${past ? 'opacity-45 border-slate-100 dark:border-slate-800' : 'border-slate-200 dark:border-slate-700'} ${e.hidden ? 'opacity-40' : ''}`}>
                                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                                        <span className={`w-2 h-2 rounded-full ${st.dot} flex-shrink-0`}></span>
                                        <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                                            {e.until
                                                ? `${+e.date.slice(8)} ${namaBulan(+e.date.slice(5, 7)).slice(0, 3)} – ${+e.until.slice(8)} ${namaBulan(+e.until.slice(5, 7)).slice(0, 3)}`
                                                : `${+e.date.slice(8)} ${namaBulan(view.m)} · ${namaHari(e.date)}`}
                                        </span>
                                        {!past && away >= 0 && away <= 7 && (
                                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-300">
                                                {countdownLabel(T, away)}
                                            </span>
                                        )}
                                        {e.hidden && <span className="text-[10px] text-slate-400">{T('dashboard.campaign.hiddenTag', '(disorok)')}</span>}
                                    </div>
                                    <p className={`text-sm font-bold leading-snug ${e.tier === 'big' ? st.text : 'text-slate-800 dark:text-white'}`}>{e.title}</p>
                                    {note && <p className="text-[11px] text-slate-600 dark:text-slate-300 mt-1 leading-relaxed">{note}</p>}
                                    {!past && away > 0 && (
                                        <p className={`text-[11px] mt-1 font-medium ${daysBetween(todayKey, prep) > 0 ? 'text-indigo-500 dark:text-indigo-400' : 'text-rose-500'}`}>
                                            {daysBetween(todayKey, prep) > 0
                                                ? T('dashboard.campaign.startContent',
                                                    `Mula konten ${+prep.slice(8)} ${namaBulan(+prep.slice(5, 7))}`,
                                                    { tarikh: `${+prep.slice(8)} ${namaBulan(+prep.slice(5, 7))}` })
                                                : T('dashboard.campaign.prepStarted', 'Masa sedia konten sudah bermula')}
                                        </p>
                                    )}
                                </button>
                            );
                        })}
                        {visibleList.length === 0 && (
                            <p className="text-sm text-slate-400 text-center py-8">
                                {selectedDay
                                    ? T('dashboard.campaign.emptyDayShort', 'Tiada event pada hari ini.')
                                    : T('dashboard.campaign.emptyMonth', 'Tiada event bulan ini.')}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Senarai edit (admin) ── */}
            {editing && (
                <div className="mt-4 bg-white dark:bg-slate-900 rounded-2xl border border-indigo-200 dark:border-indigo-500/30 p-4">
                    <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                        <h3 className="text-sm font-bold text-slate-800 dark:text-white">
                            {T('dashboard.campaign.eventsOf', `Event ${namaBulan(view.m)} ${view.y}`,
                                { bulan: namaBulan(view.m), tahun: view.y })}
                        </h3>
                        <button onClick={addCustom}
                            className="px-2.5 py-1 rounded-lg border border-indigo-200 dark:border-indigo-500/40 text-[12px] font-semibold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10">
                            ＋ {T('dashboard.campaign.newEvent', 'Event baharu')}
                        </button>
                    </div>

                    <Field label={T('dashboard.campaign.introLabel', 'Ayat pengenalan (atas kalendar)')}>
                        <input type="text" value={draft.intro} onChange={e => mutate(d => { d.intro = e.target.value; })} className={inputCls} />
                    </Field>

                    <div className="mt-4 space-y-2">
                        {listed.map(e => {
                            const st = TIER_STYLE[e.tier];
                            const custom = e.kind === 'custom';
                            return (
                                <div key={e.key} className={`rounded-xl border p-3 ${e.hidden ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 opacity-70' : 'border-slate-200 dark:border-slate-700'}`}>
                                    <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className={`w-2 h-2 rounded-full ${st.dot} flex-shrink-0`}></span>
                                            <span className={`text-sm font-bold text-slate-800 dark:text-white truncate ${e.hidden ? 'line-through' : ''}`}>{e.title || T('dashboard.campaign.noTitle', '(tanpa tajuk)')}</span>
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 flex-shrink-0">{T(KIND_LABEL[e.kind].kunci, KIND_LABEL[e.kind].label)}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 flex-shrink-0">
                                            <button onClick={() => toggleHidden(e.key)}
                                                className="px-2 py-0.5 rounded border border-slate-300 dark:border-slate-600 text-[11px] font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
                                                {e.hidden
                                                    ? T('dashboard.campaign.showAgain', 'Papar semula')
                                                    : T('dashboard.campaign.hide', 'Sorok')}
                                            </button>
                                            {custom && (
                                                <button onClick={() => removeCustom(e.id)}
                                                    className="px-2 py-0.5 rounded border border-rose-200 dark:border-rose-500/40 text-[11px] font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10">
                                                    {T('dashboard.campaign.delete', 'Padam')}
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                                        {custom && (
                                            <>
                                                <Field label={T('dashboard.campaign.fTitle', 'Tajuk')}>
                                                    <input type="text" value={e.title} onChange={ev => setCustom(e.id, 'title', ev.target.value)} className={inputCls} placeholder={T('dashboard.campaign.phTitle', 'Cth: Kempen Raya')} />
                                                </Field>
                                                <Field label={T('dashboard.campaign.fDate', 'Tarikh')}>
                                                    <input type="date" value={e.date} onChange={ev => setCustom(e.id, 'date', ev.target.value)} className={inputCls} />
                                                </Field>
                                                <Field label={T('dashboard.campaign.fUntil', 'Sampai (pilihan)')}>
                                                    <input type="date" value={e.until || ''} onChange={ev => setCustom(e.id, 'until', ev.target.value || null)} className={inputCls} />
                                                </Field>
                                            </>
                                        )}
                                        {/* Keutamaan boleh diubah untuk SEMUA event, termasuk yang auto —
                                            supaya penilaian "besar/kecil" ikut pengalaman anda, bukan tekaan kod. */}
                                        <Field label={T('dashboard.campaign.fPriority', 'Keutamaan')}>
                                            <select
                                                value={e.tier}
                                                onChange={ev => custom ? setCustom(e.id, 'tier', ev.target.value) : setTier(e.key, ev.target.value)}
                                                className={inputCls}>
                                                <option value="big">{T('dashboard.campaign.tierBigOpt', 'Besar (merah)')}</option>
                                                <option value="mid">{T('dashboard.campaign.tierMidOpt', 'Sederhana (kuning)')}</option>
                                                <option value="small">{T('dashboard.campaign.tierSmallOpt', 'Kecil (kelabu)')}</option>
                                            </select>
                                        </Field>
                                    </div>

                                    <Field label={custom
                                        ? T('dashboard.campaign.fNote', 'Nota')
                                        : T('dashboard.campaign.fNoteExtra', `Nota tambahan — ${tarikhPenuh(e.date)}`, { tarikh: tarikhPenuh(e.date) })}>
                                        <textarea
                                            value={custom ? (e.hint || '') : (draft.notes[e.key] || '')}
                                            onChange={ev => custom ? setCustom(e.id, 'hint', ev.target.value) : setNote(e.key, ev.target.value)}
                                            rows={2}
                                            placeholder={T('dashboard.campaign.phNote', 'Cth: Pulun baju tema Merdeka, mula 17 Ogos. Fokus niche fesyen.')}
                                            className={`${inputCls} resize-y`} />
                                    </Field>
                                </div>
                            );
                        })}
                        {listed.length === 0 && (
                            <p className="text-sm text-slate-400 text-center py-6">{T('dashboard.campaign.emptyMonth', 'Tiada event bulan ini.')}</p>
                        )}
                    </div>
                </div>
            )}

            {/* ── Modal butiran hari ── */}
            {dayModal && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDayModal(null)}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 w-full max-w-md max-h-[80vh] flex flex-col shadow-xl" onClick={ev => ev.stopPropagation()}>
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800">
                            <div>
                                <p className="text-sm font-bold text-slate-800 dark:text-white">{tarikhPenuh(dayModal)}</p>
                                <p className="text-[11px] text-slate-400">{namaHari(dayModal)} · {countdownLabel(T, daysBetween(todayKey, dayModal))}</p>
                            </div>
                            <button onClick={() => setDayModal(null)} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400">
                                <span className="material-symbols-outlined text-[18px]">close</span>
                            </button>
                        </div>
                        <div className="overflow-y-auto p-4 space-y-3">
                            {(dayMap[dayModal] || []).map(e => {
                                const st = TIER_STYLE[e.tier];
                                const prep = prepDate(e);
                                const note = e.kind === 'custom' ? e.hint : e.note;
                                return (
                                    <div key={e.key} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <span className="material-symbols-outlined text-[16px] text-slate-400">{KIND_ICON[e.kind]}</span>
                                            <span className="text-sm font-bold text-slate-800 dark:text-white">{e.title}</span>
                                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${st.pill}`}>{T(st.kunci, st.label)}</span>
                                        </div>
                                        <p className="text-[11px] text-slate-400 mb-2">
                                            {T(KIND_LABEL[e.kind].kunci, KIND_LABEL[e.kind].label)}
                                            {e.until ? ` · ${tarikhPenuh(e.date)} – ${tarikhPenuh(e.until)}` : ''}
                                            {e.scope ? ` · ${e.scope}` : ''}
                                        </p>
                                        {e.hint && e.kind !== 'custom' && (
                                            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{e.hint}</p>
                                        )}
                                        {note && (
                                            <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed mt-2 pl-3 border-l-2 border-indigo-300 dark:border-indigo-500/50">{note}</p>
                                        )}
                                        {daysBetween(todayKey, e.date) > 0 && (
                                            <p className={`text-[11px] mt-2 font-medium ${daysBetween(todayKey, prep) > 0 ? 'text-indigo-500 dark:text-indigo-400' : 'text-rose-500'}`}>
                                                {daysBetween(todayKey, prep) > 0
                                                    ? T('dashboard.campaign.startPrep',
                                                        `Mula sedia konten ${tarikhPenuh(prep)} (${e.lead} hari sebelum)`,
                                                        { tarikh: tarikhPenuh(prep), n: e.lead })
                                                    : T('dashboard.campaign.prepStartedLong', 'Masa sedia konten sudah bermula — jangan tunggu lagi')}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                            {(dayMap[dayModal] || []).length === 0 && (
                                <p className="text-sm text-slate-400 text-center py-6">{T('dashboard.campaign.emptyDay', 'Tiada event pada tarikh ini.')}</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default CampaignCalendarSection;
